import { inView } from 'motion';
import whenPageShown from '../components/whenPageShown';

// Scroll reveal of the content blocks. The effects themselves are CSS (scss/layout/components/_reveal.scss, keyed by
// data-reveal, data-reveal-easing and data-reveal-strength, which RevealTwigExtension writes from the "Effects" configuration);
// this starts them once an element is scrolled into view and takes everything away again when they are done.

const SELECTOR = '[data-reveal]';
const ATTRIBUTES = ['data-reveal', 'data-reveal-easing', 'data-reveal-strength', 'data-reveal-duration', 'data-reveal-delay', 'data-reveal-step', 'data-reveal-order'];
const VARIABLES = ['--reveal-duration', '--reveal-delay', '--reveal-ease'];
const OFFSET = 120; // px the element has to be inside the viewport before its effect starts
const MAX_DELAY = 2400; // ms the last element of a block may wait at most (longer waits are compressed, not cut off)
const PHASE_GAP = 150; // ms between two phases (heading, text, main elements, links) when the phase has no step of its own
const MAIN_ORDER = 4; // phase of elements without data-reveal-order (see EffectProfileResolver)
const SETTLE_MARGIN = 100; // ms after the end of the effect until the element is released
const IMAGE_WAIT = 1500; // ms an effect waits for the images inside it at most
// "mixed": every element gets its own effect, in this order
const MIX = ['zoom', 'roll', 'fade-up', 'tilt', 'blur', 'fade-left', 'fade-right', 'reveal'];

const observed = new WeakSet();
const pending = new Set();
let queue = [];
let frame = 0;
let mixIndex = 0;

const number = (value, fallback = 0) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
};

// back to a plain element: no hidden start state, no transition of its own over the one of the page (hover effects etc.)
const release = (element) => {
    ATTRIBUTES.forEach((name) => element.removeAttribute(name));
    VARIABLES.forEach((name) => element.style.removeProperty(name));
    element.classList.remove('is-revealed');
    if ('' === element.getAttribute('style')) {
        element.removeAttribute('style');
    }
};

const start = (element, delay) => {
    const duration = number(element.dataset.revealDuration, 900);
    const easing = (element.dataset.revealEasing || 'spring').replace(/[^a-z-]/g, '');

    element.style.setProperty('--reveal-duration', `${duration}ms`);
    element.style.setProperty('--reveal-delay', `${delay}ms`);
    element.style.setProperty('--reveal-ease', `var(--reveal-ease-${easing})`);
    element.classList.add('is-revealed');
    pending.delete(element);
    window.setTimeout(() => release(element), duration + delay + SETTLE_MARGIN);
};

// Images load lazily: an effect that starts before its image is there animates an empty frame and the image then just
// appears. So the effect waits for the images inside the element (but never longer than IMAGE_WAIT).
// complete: loaded or failed; a lazy image the browser has not started yet is not complete
const imageReady = (image) => image.complete;

// images of slides that are not shown (swiper) are not loaded and must not hold anything up
const imageNear = (image) => {
    const box = image.getBoundingClientRect();

    return box.width > 0 && box.right > 0 && box.left < window.innerWidth && box.bottom > -200 && box.top < window.innerHeight + 200;
};

const imagesReady = (elements) => new Promise((resolve) => {
    const waiting = elements.flatMap((element) => [...element.querySelectorAll('img')]).filter((image) => imageNear(image) && !imageReady(image));
    if (0 === waiting.length) {
        resolve();

        return;
    }
    let left = waiting.length;
    const done = () => {
        left -= 1;
        if (left <= 0) {
            resolve();
        }
    };
    waiting.forEach((image) => {
        image.addEventListener('load', done, {once: true});
        image.addEventListener('error', done, {once: true});
    });
    window.setTimeout(resolve, IMAGE_WAIT);
});

const flush = () => {
    frame = 0;
    whenPageShown(run);
};

const order = (element) => number(element.dataset.revealOrder, MAIN_ORDER);
const byDocument = (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

// Delays of the elements of one block (scope) that come into view together. They appear in phases (data-reveal-order:
// heading lines, text, main elements, links), each phase in reading order; within a phase element i starts i steps
// after the first ("one after the other", over the whole grid, not per row). The next phase begins one step (or
// PHASE_GAP) after the last start of the previous one. Rows scrolled to later begin again from the front.
// Elements of one block that come into view a few frames apart (title, then subtitle) continue the plan of the block
// instead of starting from the front again: scope => time (performance.now) at which the next phase may begin.
const scopeFree = new WeakMap();

const schedule = (scope, elements) => {
    elements.sort((a, b) => order(a) - order(b) || byDocument(a, b));

    const now = performance.now();
    const plan = [];
    let time = Math.max(0, (scopeFree.get(scope) ?? 0) - now);
    let index = 0;
    while (index < elements.length) {
        const phase = order(elements[index]);
        let lastStart = time;
        let maxStep = 0;
        for (let position = 0; index < elements.length && order(elements[index]) === phase; index += 1, position += 1) {
            const element = elements[index];
            const step = number(element.dataset.revealStep);
            const begin = time + position * step;
            plan.push([element, begin + number(element.dataset.revealDelay)]);
            lastStart = Math.max(lastStart, begin);
            maxStep = Math.max(maxStep, step);
        }
        time = lastStart + (maxStep > 0 ? maxStep : PHASE_GAP);
    }

    // a long wait is compressed for the whole block, so the order stays the same
    const longest = Math.max(0, ...plan.map(([, delay]) => delay));
    const factor = longest > MAX_DELAY ? MAX_DELAY / longest : 1;
    scopeFree.set(scope, now + time * factor);

    return plan.map(([element, delay]) => [element, Math.round(delay * factor)]);
};

const run = () => {
    const batch = queue;
    queue = [];

    imagesReady(batch).then(() => {
        batch.forEach((element) => void element.offsetWidth); // the hidden state has to be drawn once or nothing is animated

        const scopes = new Map();
        batch.forEach((element) => {
            const scope = element.closest('[data-reveal-scope]') || document;
            scopes.set(scope, [...(scopes.get(scope) ?? []), element]);
        });
        scopes.forEach((elements, scope) => schedule(scope, elements).forEach(([element, delay]) => start(element, delay)));
    });
};

const enqueue = (element) => {
    queue.push(element);
    if (!frame) {
        frame = window.requestAnimationFrame(flush);
    }
};

// An element that is moved by its effect (e.g. fade-up) is measured at the moved position, so a short element at the very end
// of the page can stay just outside the viewport and would never start. Once the end of the page is reached nothing is left to wait for.
const revealRestAtPageEnd = () => {
    if (window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 2) {
        return;
    }
    swipers.forEach((members, swiper) => revealSwiper(swiper));
    pending.forEach(enqueue);
    pending.clear();
    window.removeEventListener('scroll', revealRestAtPageEnd);
};

// Sliders: the slides are moved by the visitor's finger, not by the effect. When the slider comes into view, only the
// slides that are visible then get their effect; the others are released at once and are simply swiped in later.
// The banner (.hero-swiper) has effects of its own and is not handled here.
const SWIPER = '.swiper:not(.hero-swiper)';
const swipers = new Map(); // swiper => elements with an effect inside it (until it came into view)

const insideSwiper = (element, swiper) => {
    const slide = element.closest('.swiper-slide') ?? element; // the slide is not moved by the effect
    const box = slide.getBoundingClientRect();
    const frame = swiper.getBoundingClientRect();

    // with the fade effect all slides lie on top of each other and only the active one is visible
    return box.width > 0 && box.right > frame.left + 1 && box.left < frame.right - 1 && '0' !== getComputedStyle(slide).opacity;
};

const revealSwiper = (swiper) => {
    const members = swipers.get(swiper);
    if (!members) {
        return;
    }
    swipers.set(swiper, null); // came into view: later members (e.g. loop clones) are released at once
    members.forEach((element) => (insideSwiper(element, swiper) ? enqueue(element) : release(element)));
};

const observeInSwiper = (element, swiper) => {
    if (swipers.has(swiper)) {
        const members = swipers.get(swiper);
        if (members) {
            members.add(element);
        } else {
            release(element);
        }

        return;
    }
    swipers.set(swiper, new Set([element]));
    const stop = inView(swiper, () => {
        revealSwiper(swiper);
        stop();
    }, {margin: `0px 0px -${OFFSET}px 0px`});
};

const observe = (element) => {
    if (observed.has(element)) {
        return;
    }
    observed.add(element);

    // while it is still hidden every "mixed" element is given its own effect
    if ('mix' === element.getAttribute('data-reveal')) {
        element.setAttribute('data-reveal', MIX[mixIndex % MIX.length]);
        mixIndex += 1;
    }

    const swiper = element.closest(SWIPER);
    if (swiper) {
        observeInSwiper(element, swiper);

        return;
    }
    pending.add(element);

    const stop = inView(element, () => {
        if (pending.delete(element)) {
            enqueue(element);
        }
        stop();
    }, {margin: `0px 0px -${OFFSET}px 0px`});
};

const observeWithin = (root) => {
    if (root.matches(SELECTOR)) {
        observe(root);
    }
    root.querySelectorAll(SELECTOR).forEach(observe);
};

// Starts the effects of everything in a container that lies in view now. inView measures an element at the place its effect
// holds it (moved down), so a block near the lower edge of a part that does not scroll further would never start. The rooms of
// the stage page (initInfinity.js) call this when they have opened.
export function revealWithin(container) {
    container.querySelectorAll(SELECTOR).forEach((element) => {
        if (pending.has(element) && element.getBoundingClientRect().top < window.innerHeight) {
            pending.delete(element);
            enqueue(element);
        }
    });
}

export function initScrollReveal() {
    observeWithin(document.body);
    window.addEventListener('scroll', revealRestAtPageEnd, {passive: true});

    // elements added later (e.g. by scripts) get their effect as well
    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
            if (1 === node.nodeType) {
                observeWithin(node);
            }
        }));
    }).observe(document.body, {childList: true, subtree: true});
}
